// Differentiator: Die Suche IST die Startseite. Rollenbewusster Home-Screen — Such-Anker + Zuletzt +
// die im Profil erlaubten Aktionen. Mechaniker und Meister sehen verschiedene Aktions-Sets, weil ihre
// Profile verschiedene homeLayout + Funktionsrechte haben (das Personalisierungsmodell; die Suchleiste
// ist der konstante Anker).
//
// Portierung aus dem Vite-Prototyp (frontend/src/components/HomeScreen.jsx). Geaendert wurden NUR
// Styling-Idiom (v4-Token/.gx-*/text-fg/text-muted -> isDarkMode-Verzweigung mit konkreten
// Tailwind-v3-Klassen), Theme-/i18n-Hook, i18n-Keys (pm.-Praefix) und die Import-/API-Pfade.
//
// MONOLITH-API-KONVENTION (wie SearchBar.js): `user` ist das ERSTE Argument jeder API-Funktion (traegt
// das JWT) und die Funktion liefert den geparsten JSON-Body DIREKT zurueck — KEIN { data }-Wrapper.
// Daher braucht HomeScreen jetzt das `user`-Prop fuer den "Zuletzt"-Fetch.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronDown, ChevronUp, RotateCcw } from 'lucide-react';
import { useTheme } from '../../../core/ui/ThemeContext';
import { useLanguage } from '../../../core/i18n/LanguageContext';
import { resetData, getDashboard } from '../api';
import { ACTION_CATALOG } from '../actions';
import DashboardPanel from './DashboardPanel';
import PlantSelect from './PlantSelect';
import RecentEquipmentList from './RecentEquipmentList';
import SearchBar from './SearchBar';
import WeekStrip from './WeekStrip';
import { Card } from './ui';

// Sichtbarkeit des Dashboard-Panels je Geraet merken (localStorage, Default: eingeblendet).
const DASH_OPEN_KEY = 'pm.dashboardOpen';

function HomeScreen({ me, user, onStartOrder, onAction, onPlantChange }) {
  const { isDarkMode } = useTheme();
  const { t } = useLanguage();
  const [myWeek, setMyWeek] = useState(null);
  const [toast, setToast] = useState('');
  const [dashOpen, setDashOpen] = useState(() => {
    try { return localStorage.getItem(DASH_OPEN_KEY) !== '0'; } catch { return true; }
  });
  const toggleDash = () => setDashOpen((v) => {
    const next = !v;
    try { localStorage.setItem(DASH_OPEN_KEY, next ? '1' : '0'); } catch { /* privat/blockiert -> nur Session */ }
    return next;
  });

  // Toast zentral: Timer wird bei jedem neuen Toast zurueckgesetzt und beim Unmount geraeumt.
  const toastTimer = useRef();
  const showToast = useCallback((msg) => {
    setToast(msg);
    clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(''), 2500);
  }, []);
  useEffect(() => () => clearTimeout(toastTimer.current), []);

  // "Meine Woche" nur fuer Ausfuehrende (canConfirm): wieviel liegt naechste Woche auf meinem
  // Tisch (REL in meinen Arbeitsplaetzen), Ueberfaellig zuerst. Der Meister hat stattdessen die
  // Dashboard-Kachel. Optional — bei Fehler bleibt der Streifen einfach weg.
  const canConfirm = me.profile.functionRights.canConfirm;
  useEffect(() => {
    if (!canConfirm) { setMyWeek(null); return; }
    let alive = true;
    getDashboard(user, me.activePlant, me.profile.id)
      .then((res) => { if (alive) setMyWeek(res?.myWeek || null); })
      .catch(() => { if (alive) setMyWeek(null); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, me]);

  // Demo-Reset (nur Mock-Adapter, nur Meister-Profil): wischt angelegte/persistierte Daten zurueck
  // auf die Fixture-Baseline. Ersetzt das manuelle Loeschen von .data/state.json beim Testen.
  const onReset = async () => {
    if (!window.confirm(t('pm.header.resetConfirm'))) return;
    try {
      const res = await resetData(user, me.profile.id);
      showToast(res?.ok === false ? `${t('pm.order.errorPrefix')}: ${res.error}` : t('pm.home.resetDone'));
    } catch (e) {
      showToast(`${t('pm.order.errorPrefix')}: ${e?.message || 'error'}`);
    }
  };

  const { homeLayout, functionRights } = me.profile;
  // homeLayout bestimmt WELCHE Aktionen in welcher REIHENFOLGE erscheinen; ACTION_CATALOG.right gatet
  // die Sichtbarkeit gegen functionRights (autoritativ).
  const cards = homeLayout
    .map((key) => ({ key, ...ACTION_CATALOG[key] }))
    .filter((a) => a.icon && (!a.right || functionRights[a.right]));

  const onCard = (a) => {
    if (a.built) return onAction(a.key);
    showToast(t('pm.home.notActive', { label: t(`pm.action.${a.key}.label`) }));
  };

  return (
    <div className="space-y-6">
      {/* Kopfzeile der Startseite: links der Dashboard-Umschalter (nur Meister, VERSALIEN wie die
          Sektions-Titel), rechts IMMER das aktive Werk (PlantSelect, Lager-Muster) — unabhaengig
          davon, ob das Dashboard ein- oder ausgeblendet ist. */}
      <section>
        <div className={`flex items-center gap-3 ${me.profile.functionRights.canApprove ? 'justify-between' : 'justify-end'}`}>
          {me.profile.functionRights.canApprove && (
            <button
              onClick={toggleDash}
              aria-expanded={dashOpen}
              className={`flex items-center gap-1 text-xs font-semibold uppercase tracking-wide ${isDarkMode ? 'text-white/60' : 'text-slate-500'}`}
            >
              {dashOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
              {dashOpen ? t('pm.dash.hide') : t('pm.dash.show')}
            </button>
          )}
          <PlantSelect me={me} onPlantChange={onPlantChange} />
        </div>
        {/* Zugeklappt wird nichts geladen — das Panel mountet erst beim Aufklappen. */}
        {me.profile.functionRights.canApprove && dashOpen && (
          <div className="mt-2">
            <DashboardPanel me={me} user={user} onOpenList={onAction} />
          </div>
        )}
      </section>

      <section>
        <h1 className={`text-2xl font-bold mb-1 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
          {t('pm.home.title')}
        </h1>
        <p className={`mb-3 ${isDarkMode ? 'text-white/60' : 'text-slate-500'}`}>{t('pm.home.subtitle')}</p>
        <SearchBar user={user} role={me.profile.id} onSelect={(eq) => onStartOrder(eq)} />
      </section>

      {/* Meine Woche (nur canConfirm-Profile) — Klick fuehrt in "Meine Auftraege". */}
      {myWeek && (
        <section>
          <WeekStrip title={t('pm.week.my')} week={myWeek} onClick={() => onAction('my-orders')} />
        </section>
      )}

      {/* Zuletzt verwendet: ECHTE Nutzungshistorie des Users (recency.js) statt der frueheren
          Fixture-Attrappe (Backlog D3 erledigt). Klick startet wie bisher einen Auftrag. */}
      <RecentEquipmentList user={user} me={me} max={5} onSelect={(eq) => onStartOrder(eq)} />

      <section>
        <h2 className={`text-sm font-semibold uppercase tracking-wide mb-2 ${isDarkMode ? 'text-white/50' : 'text-slate-500'}`}>
          {t('pm.home.actions')}
        </h2>
        <div className="grid grid-cols-2 gap-3">
          {cards.map((a) => {
            const Icon = a.icon;
            const hero = a.key === 'create-order';
            return (
              <button key={a.key} onClick={() => onCard(a)} className="text-left">
                <Card className={`h-full ${hero ? 'ring-2 ring-brand' : ''}`}>
                  <Icon className={hero
                    ? 'text-brand'
                    : (isDarkMode ? 'text-white' : 'text-slate-900')} size={28} />
                  <div className={`mt-2 font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                    {t(`pm.action.${a.key}.label`)}
                  </div>
                  <div className={`text-xs ${isDarkMode ? 'text-white/60' : 'text-slate-500'}`}>
                    {t(`pm.action.${a.key}.sub`)}
                  </div>
                  {!a.built && (
                    <span className={`text-[10px] mt-1 inline-block rounded px-1.5 py-0.5 ${isDarkMode
                      ? 'bg-white/[0.08] text-white/60'
                      : 'bg-slate-100 text-slate-500'
                    }`}>
                      {t('pm.home.soon')}
                    </span>
                  )}
                </Card>
              </button>
            );
          })}
        </div>
      </section>

      {/* Demo-Reset — bewusst unauffaellig am Seitenende, nur fuer Profile mit canManageUsers. */}
      {me.profile.functionRights.canManageUsers && (
        <section className="pt-2 text-center">
          <button
            onClick={onReset}
            className={`inline-flex items-center gap-1.5 text-xs font-medium transition-colors ${isDarkMode
              ? 'text-white/40 hover:text-white/70'
              : 'text-slate-400 hover:text-slate-600'
            }`}
          >
            <RotateCcw size={14} /> {t('pm.header.reset')}
          </button>
        </section>
      )}

      {toast && (
        <div role="status" className={`fixed bottom-6 left-1/2 -translate-x-1/2 text-sm rounded-full px-4 py-2 shadow-lg z-30 ${isDarkMode
          ? 'bg-gray-900 text-white border border-white/10'
          : 'bg-slate-900 text-white border border-slate-700'
        }`}>
          {toast}
        </div>
      )}
    </div>
  );
}

export default HomeScreen;
